// Mapping pur entre la saisie manuelle du contrat complementaire (modele local)
// et la structure backend facfse.Ident.Cc (PascalCase, 1re lettre majuscule).
//
// Reference legacy : F:\SV\PROJETDOTNET\TransCAM_Interface\fcompmanuel.vb
//   (frmcompmanuel : saisie manuelle du contrat complementaire)
// Structure back   : F:\SV\PROJETDOTNET\TransCAM_Library\Strfse.cs
//   (struct complementairemutuelle, l.104-171)
//
// Le back serialise en PascalCase via un NamingStrategy qui ne met QUE la 1re
// lettre en majuscule (cf memoire projet facfse-forme-prest-pascalcase) -> les
// champs de cc deviennent : Modegestion, Contrat, Numeroamc... ATTENTION : la
// strategie garde la casse interne d'origine, donc `numeroAMC` -> `NumeroAMC`,
// `identifiantassureAMC` -> `IdentifiantassureAMC`, `Routage` reste `Routage`.
// Le mapping reste DEFENSIF (plusieurs casses tentees) car la forme exacte de
// Ident.Cc cote web n'est pas confirmee par un dump.

import config from '../config.js';
import RequestManager from './request.js';

// --- Modes de gestion (cc.modegestion) ---
export const MODE_INCONNU = 0;
export const MODE_UNIQUE = 1;   // gestion unique (routage RO)
export const MODE_SEPAREE = 2;  // gestion separee (routage AMC propre)

// Routages les plus courants (Text3 du legacy est un champ libre de 2 car. ;
// on propose une aide a la saisie sans la rendre exclusive).
export const ROUTAGES = [
    { valeur: 'RO', libelle: 'RO - Gestion unique (regime obligatoire)' },
    { valeur: 'GU', libelle: 'GU - Gestion unique' },
    { valeur: 'DR', libelle: 'DR - Gestion separee (routage direct)' },
];

function s(v) { return v == null ? '' : String(v).trim(); }
function up(v) { return s(v).toUpperCase(); }

// Acces defensif : retourne la 1re cle non nulle parmi plusieurs casses possibles.
function pick(obj, ...cles) {
    if (!obj) return undefined;
    for (const c of cles) {
        if (obj[c] !== undefined && obj[c] !== null) return obj[c];
    }
    return undefined;
}

// Recupere le sous-objet complementaire (cc) du facfse, quelle que soit la casse.
export function getCc(facfse) {
    const ident = pick(facfse, 'Ident', 'ident');
    if (!ident) return null;
    return pick(ident, 'Cc', 'cc') || null;
}

// Modele local vierge (= champs du formulaire fcompmanuel).
export function complementaireVide() {
    return {
        modeGestion: MODE_UNIQUE,
        contrat: '',
        noAdherent: '',
        noAssureAMC: '',
        numeroAMC: '',            // gestion unique
        routage: 'RO',            // unique : force RO ; separee : editable
        domaine: '',              // gestion separee
        hote: '',                 // gestion separee
        typeConvention: '',       // gestion separee
        critSec: '',              // gestion separee
        indicateurTraitement: '',
        codeSTS: '',
        debutValidite: '',        // validite : informatif (hors struct cc)
        finValidite: '',
        attestationPresentee: true,
        priseEnCharge: false,
        nonAccordAMO: false,      // gestion separee
        horsTP: false,            // gestion separee (hors tiers payant)
    };
}

// Lecture : facfse.Ident.Cc -> modele local.
export function complementaireDepuisFacfse(facfse) {
    const c = complementaireVide();
    const cc = getCc(facfse);
    if (!cc) return c;

    c.contrat = s(pick(cc, 'Contrat', 'contrat'));
    c.noAdherent = s(pick(cc, 'Noadherent', 'noadherent'));
    c.noAssureAMC = s(pick(cc, 'IdentifiantassureAMC', 'identifiantassureAMC'));
    c.numeroAMC = s(pick(cc, 'NumeroAMC', 'numeroAMC', 'Numeroamc'));
    c.routage = up(pick(cc, 'Routage', 'routage')) || 'RO';
    c.domaine = s(pick(cc, 'Domaine', 'domaine'));
    c.hote = s(pick(cc, 'Hote', 'hote'));
    c.typeConvention = up(pick(cc, 'Typeconvention', 'typeconvention'));
    c.critSec = s(pick(cc, 'Criteresecondaire', 'criteresecondaire'));
    c.indicateurTraitement = s(pick(cc, 'Indictrt', 'indictrt'));
    c.codeSTS = up(pick(cc, 'Typeservice', 'typeservice'));

    const piece = s(pick(cc, 'Piecejustif', 'piecejustif'));
    c.attestationPresentee = piece === '1';
    const ref = s(pick(cc, 'Referencejustif', 'referencejustif'));
    c.priseEnCharge = ref !== '';
    const nonAcc = s(pick(cc, 'NonaccordAMO', 'nonaccordAMO'));
    c.nonAccordAMO = nonAcc === '1';

    // Mode de gestion : valeur explicite sinon deduite du routage (cf
    // fcompmanuel_Load : routage RO/GU -> gestion unique).
    const mode = Number(pick(cc, 'Modegestion', 'modegestion'));
    if (mode === MODE_UNIQUE || mode === MODE_SEPAREE) {
        c.modeGestion = mode;
    } else {
        c.modeGestion = (c.routage === 'RO' || c.routage === 'GU') ? MODE_UNIQUE : MODE_SEPAREE;
    }
    return c;
}

// Formatage a la sortie de champ, fidele au legacy.
export function formatIndicateur(v) {
    const t = s(v);
    return /^\d+$/.test(t) ? String(parseInt(t, 10)).padStart(2, '0') : t;
}
export function formatHote(v) {
    const t = s(v);
    return /^\d+$/.test(t) ? String(parseInt(t, 10)).padStart(3, '0') : t;
}

// Ecriture : modele local -> facfse.Ident.Cc (mutation directe sur le facfse
// fourni ; l'appelant travaille sur une copie defensive avant setFacfse).
// Retourne le sous-objet cc mute (ou null si pas d'Ident exploitable).
export function appliquerComplementaireDansFacfse(facfse, c) {
    if (!facfse || !c) return null;
    const ident = pick(facfse, 'Ident', 'ident');
    if (!ident) return null;
    // cree cc si absent (cle PascalCase par defaut, comme le reste du facfse).
    let cc = pick(ident, 'Cc', 'cc');
    if (!cc) { cc = {}; ident.Cc = cc; }

    const unique = c.modeGestion === MODE_UNIQUE;

    cc.Modegestion = c.modeGestion;
    cc.Contrat = up(c.contrat);
    cc.Noadherent = s(c.noAdherent);
    cc.IdentifiantassureAMC = s(c.noAssureAMC);
    cc.Indictrt = formatIndicateur(c.indicateurTraitement);
    cc.Typeservice = up(c.codeSTS);
    cc.Piecejustif = c.attestationPresentee ? '1' : '';

    if (unique) {
        // Gestion unique : routage force RO, numero AMC renseigne, champs de
        // gestion separee neutralises (caches dans le legacy).
        cc.Routage = 'RO';
        cc.NumeroAMC = s(c.numeroAMC);
        cc.Domaine = '';
        cc.Hote = '';
        cc.Typeconvention = '';
        cc.Criteresecondaire = '';
        cc.NonaccordAMO = '';
    } else {
        cc.Routage = up(c.routage);
        cc.Domaine = s(c.domaine);
        cc.Hote = formatHote(c.hote);
        cc.Typeconvention = up(c.typeConvention);
        cc.Criteresecondaire = s(c.critSec);
        cc.NonaccordAMO = c.nonAccordAMO ? '1' : '';
    }
    return cc;
}

// Validation cote saisie, miroir leger des controles de fcompmanuel
// (Command1_Click + Text8/Text9). Retourne { ok, erreurs[] }.
export function validerComplementaire(c) {
    const erreurs = [];
    if (!s(c.contrat)) {
        erreurs.push('Contrat complementaire vide, veuillez saisir un numero de contrat valide.');
    }
    if (c.modeGestion === MODE_SEPAREE && !s(c.routage)) {
        erreurs.push('Routage obligatoire en gestion separee.');
    }
    if (c.debutValidite && !dateValide(c.debutValidite)) {
        erreurs.push('Date de debut de validite invalide (jj/mm/aaaa).');
    }
    if (c.finValidite && !dateValide(c.finValidite)) {
        erreurs.push('Date de fin de validite invalide (jj/mm/aaaa).');
    }
    return { ok: erreurs.length === 0, erreurs };
}

// Validation d'une date jj/mm/aaaa (existence reelle du jour). Vide = accepte.
export function dateValide(valeur) {
    const v = s(valeur);
    if (v === '') return true;
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
    if (!m) return false;
    const jj = +m[1], mm = +m[2], aaaa = +m[3];
    if (mm < 1 || mm > 12 || jj < 1 || jj > 31) return false;
    const d = new Date(aaaa, mm - 1, jj);
    return d.getFullYear() === aaaa && d.getMonth() === mm - 1 && d.getDate() === jj;
}

// Validation SERVEUR de la part complementaire via la logique metier PARTAGEE avec
// le desktop : backend POST FSE/validerComplementaire -> Interface_entree_complementaire.
// valider_parametres_complementaires_manuels (recherche_OC sur la table mutuelles4, puis
// annuaire AMC sous APCV). Cette fonction MUTE facfse.Ident.Cc cote serveur (routage/
// domaine/hote/indicateur/piece justificative normalises) -> l'appelant doit avoir pousse
// le facfse AVANT (setFacfse) puis le rafraichir APRES (fetchFacfse) en cas de succes.
// Les messages d'erreur metier remontent via le buffer popup (GET /Erreur) ; le retour porte
// le detail : { ok, enErreur, routage, indicateurDeTraitement, codeSTS,
//   miseAJourContratComplementaire, forcageDRE }.
export async function validerComplementaireBackend(c, choixAnnuaireIndex = -1) {
    const unique = c.modeGestion === MODE_UNIQUE;
    const payload = {
        // Choix de convention annuaire (2e passe) : index dans la liste renvoyee par une precedente
        // validation qui avait signale choixAnnuaireRequis. -1 = 1re passe (aucun choix).
        ChoixAnnuaireIndex: (typeof choixAnnuaireIndex === 'number' ? choixAnnuaireIndex : -1),
        // Drapeaux de contexte (signature valider_parametres_complementaires_manuels).
        GestionUnique: unique,
        EnCMU: false,
        EnAME: false,
        ActeEnSerie: false,
        DreSsFse: false,
        // Parametres du contrat complementaire (mappes sur parametresComplementaireStructure).
        IndexBeneficiaire: 0,               // 0 => beneficiaire courant (benec) cote serveur
        Contrat: up(c.contrat),
        NoAdherent: s(c.noAdherent),
        NoAssureAMC: s(c.noAssureAMC),
        NumeroAMC: s(c.numeroAMC),
        Routage: unique ? 'RO' : up(c.routage),
        Domaine: unique ? '' : s(c.domaine),
        Hote: unique ? '' : formatHote(c.hote),
        Type: unique ? '' : up(c.typeConvention),
        CritSec: unique ? '' : s(c.critSec),
        IndicateurDeTraitement: formatIndicateur(c.indicateurTraitement),
        CodeSTS: up(c.codeSTS),
        DebutValidite: s(c.debutValidite),
        FinValidite: s(c.finValidite),
        ReferencePriseEnChargeAmc: '',
        AttestationPresentee: !!c.attestationPresentee,
        PriseEnCharge: !!c.priseEnCharge,
        ReponseDevaforableAccordAMO: unique ? false : !!c.nonAccordAMO,
        HorsTP: unique ? false : !!c.horsTP,
        TagDonneesFiches: false,
        ForcageTopM: false,
        ModifAutoContrat: false,
    };
    return RequestManager.post(`${config.urlBackEnd}FSE/validerComplementaire`, payload);
}
